import { Trash2, History, UserRound } from 'lucide-react'
import type { RuleAction, RuleEngineRunHistoryEntry } from '../../types/rulesEngine'
import { useTheme } from '../../contexts/ThemeContext'
import Button from '../forms/Button'

const OUTCOME_STYLE: Record<
  RuleAction,
  { label: string; color: string; bg: string }
> = {
  approve: { label: 'Approved', color: '#0d9488', bg: '#f0fdfa' },
  deny: { label: 'Denied', color: '#dc2626', bg: '#fef2f2' },
  route_prior_auth: { label: 'Prior Auth', color: '#d97706', bg: '#fffbeb' },
  flag: { label: 'Flagged', color: '#2563eb', bg: '#eff6ff' },
  continue: { label: 'Continue', color: '#475569', bg: '#f8fafc' },
}

function formatRanAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

type RulesEngineHistoryPanelProps = {
  entries: RuleEngineRunHistoryEntry[]
  onSelect: (entry: RuleEngineRunHistoryEntry) => void
  onDelete: (id: string) => void
  onClearAll: () => void
}

export default function RulesEngineHistoryPanel({
  entries,
  onSelect,
  onDelete,
  onClearAll,
}: RulesEngineHistoryPanelProps) {
  const { theme } = useTheme()

  if (entries.length === 0) {
    return (
      <div
        className="rounded-2xl border border-dashed py-14 text-center"
        style={{ borderColor: theme.colors.border }}
      >
        <History className="mx-auto mb-2 h-8 w-8" style={{ color: theme.colors.textMuted }} />
        <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
          No run history yet
        </p>
        <p className="mt-1 text-xs" style={{ color: theme.colors.textMuted }}>
          Each time you run the engine for a patient, it will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: theme.colors.textMuted }}>
          {entries.length} run{entries.length === 1 ? '' : 's'} recorded
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onClearAll}>
          Clear all
        </Button>
      </div>

      <div className="space-y-2">
        {entries.map((entry) => {
          const outcome = OUTCOME_STYLE[entry.outcome] ?? OUTCOME_STYLE.flag
          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => onSelect(entry)}
              className="group w-full rounded-xl border p-4 text-left transition-shadow hover:shadow-md"
              style={{
                borderColor: theme.colors.cardBorder,
                backgroundColor: theme.colors.cardBackground,
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                    style={{ backgroundColor: theme.colors.background, color: theme.colors.textSecondary }}
                  >
                    <UserRound className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>
                      {entry.patientName}
                    </p>
                    <p className="truncate text-xs" style={{ color: theme.colors.textSecondary }}>
                      {entry.payor ?? '—'}
                      {entry.memberId ? ` · ${entry.memberId}` : ''}
                    </p>
                    <p className="mt-1 text-xs" style={{ color: theme.colors.textMuted }}>
                      {entry.ruleSetName} · {entry.rulesEvaluated} rule(s) · {formatRanAt(entry.ranAt)}
                    </p>
                    {entry.message ? (
                      <p className="mt-1 truncate text-[11px] italic" style={{ color: theme.colors.textSecondary }}>
                        {entry.message}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className="rounded-full px-2.5 py-1 text-[11px] font-bold"
                    style={{ color: outcome.color, backgroundColor: outcome.bg }}
                  >
                    {outcome.label}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(entry.id)
                    }}
                    className="rounded-lg p-1.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-red-50"
                    style={{ color: '#dc2626' }}
                    aria-label="Delete history entry"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
